import * as React from "react"
import { categoryData } from "@types"
import { generateImageUrl } from "@utils"
import "animate.css"
import "animate.css/animate.compat.css"
import ScrollAnimation from "react-animate-on-scroll"
import { useEffect, useRef, useLayoutEffect, useMemo } from "react"
import { StaticImage } from "gatsby-plugin-image"

interface folioProps {
  folios: categoryData[] | undefined
  boxSize: number
}

const useUniqueObfuscatedClasses = (items: any, boxSize: number) => {
  const classNames = useMemo(
    () => items?.map(() => `x${Math.random().toString(36).slice(2, 12)}`),
    [items?.length]
  )

  useLayoutEffect(() => {
    const style = document.createElement("style")
    let css = ""

    classNames?.forEach((className: string, index: number) => {
      css += `
        .${className} {
          width: calc(100% / ${boxSize});
          padding-right: 1rem;
          padding-left: 0.25rem;
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
          transition: all 0.5s;
        }
      `
    })

    style.textContent = css
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [classNames, boxSize])

  return classNames
}
const useObfuscatedClass = (baseStyles: string, hoverStyles: string = "") => {
  const className = useMemo(
    () => `x${Math.random().toString(36).slice(2, 12)}`,
    []
  )

  useLayoutEffect(() => {
    const style = document.createElement("style")

    let css = `
      .${className} {
        ${baseStyles}
      }
    `

    if (hoverStyles) {
      css += `
        .${className}:hover {
          ${hoverStyles}
        }
      `
    }

    style.textContent = css
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [className, baseStyles, hoverStyles])

  return className
}
export const Folio = ({ folios, boxSize }: folioProps) => {
  const classNames = useUniqueObfuscatedClasses(folios, boxSize)
  const LazyImage = ({ src, alt }: any) => {
    const imgRef = useRef<HTMLImageElement | null>(null) // Specify the type for TypeScript

    useEffect(() => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = imgRef.current // Get the current image reference
            if (img) {
              img.src = src // Set the src when in view
              observer.unobserve(img) // Unobserve the image element
            }
          }
        })
      })

      if (imgRef.current) {
        observer.observe(imgRef.current) // Observe the image element
      }

      return () => {
        if (imgRef.current) {
          observer.unobserve(imgRef.current) // Clean up the observer
        }
      }
    }, [src])

    return <img ref={imgRef} alt={alt} className={imageClass} loading="lazy" />
  }
  // obfuscate classes
  const imageClass = useObfuscatedClass(`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.5rem;
    `)
  const boxClass = useObfuscatedClass(
    `
    width: 100%;
    transform: scale(1);
    transition: all 150ms ease-in-out;
    transition-delay: 150ms;
    position: relative;
    border-radius: 1.5rem;
    padding-top: 100%;
    box-shadow: inset 0 2px 4px 0 rgb(0 0 0 / 0.05);
    `,
    `
    transform: scale(1.02);
  `
  )
  const absoluteClass = useObfuscatedClass(`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    `)

  return (
    <div
      id="scroll"
      className={`w-full flex flex-wrap ${(folios?.length ?? 0) > 0 ? "min-h-[100vh]" : "min-h-[80vh]"}`}
    >
      {(folios?.length ?? 0) > 0 ? (
        folios?.map((data: categoryData, index: number) => (
          <div
            className={classNames[index]}
            key={index}
            data-cid={`c${Math.random().toString(36).slice(2, 8)}`}
          >
            <ScrollAnimation animateOnce animateIn="zoomIn" initiallyVisible>
              <div className={boxClass}>
                <LazyImage
                  src={generateImageUrl(data?.cardImage)}
                  alt={data?.projectName || "Project image"}
                />
                {data.fullImage !== null || data.href !== null ? (
                  <div>
                    {data.options === "href" ? (
                      <a
                        className={absoluteClass}
                        href={`${data?.href}`}
                        target="_blank"
                      ></a>
                    ) : (
                      <a
                        className={absoluteClass}
                        href={`${generateImageUrl(data?.fullImage)}`}
                        target="_blank"
                      ></a>
                    )}
                  </div>
                ) : (
                  <a
                    className="absolute inset-0"
                    href="/404"
                    target="_blank"
                  ></a>
                )}
              </div>
            </ScrollAnimation>
          </div>
        ))
      ) : (
        <>
          <div className=" w-full flex justify-center items-center">
            <div className="w-[15%]">
              <StaticImage
                className="mx-3 w-full lg:mx-0"
                src="../../images/coming-soon.png"
                alt="coming soon"
                placeholder="blurred"
                layout="fullWidth"
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
